/* eslint-disable import/prefer-default-export */
/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import isNil from 'lodash/isNil';

/**
 * This class represents the utility functions for connection status of aircraft.
 */
const MBPS_CONVERSION = 1000000;
const MBPS_STRING = 'Mbps';
const KBPS_CONVERSION = 1000;
const KBPS_STRING = 'kbps';
const BPS_STRING = 'bps';

/**
 *
 * @param bps - value to convert
 * @param numberOfDecimals - default number of decimal places
 * @param alternateNumberOfDecimals -  alternate decimal places. Must be greater than numberOfDecimals
 */

// Convert bits per second (bps) to Mega bits per second (Mbps).
export const bpsToMbps = (bps: number, numberOfDecimals = 2, alternateNumberOfDecimals = 2): string => {
  const mbps = bps / MBPS_CONVERSION;
  if (alternateNumberOfDecimals > numberOfDecimals && +mbps.toFixed(alternateNumberOfDecimals) % 1 !== 0) {
    return mbps.toFixed(alternateNumberOfDecimals);
  }
  return mbps.toFixed(numberOfDecimals);
};

// Convert bits per second (bps) to Kilo bits per second (kbps).
export const bpsToKbps = (bps: number, numberOfDecimals = 2, alternateNumberOfDecimals = 2): string => {
  const kbps = bps / KBPS_CONVERSION;
  if (alternateNumberOfDecimals > numberOfDecimals && +kbps.toFixed(alternateNumberOfDecimals) % 1 !== 0) {
    return kbps.toFixed(alternateNumberOfDecimals);
  }
  return kbps.toFixed(numberOfDecimals);
};

// Convert bits per second (bps) to the appriate unit and append the unit string
export const bpsToUnitString = (
  bps: number | null | undefined,
  numberOfDecimals = 2,
  alternateNumberOfDecimals = 2,
): string => {
  if (isNil(bps)) {
    return '';
  }

  if (bps >= MBPS_CONVERSION) {
    return `${bpsToMbps(bps, numberOfDecimals, alternateNumberOfDecimals)} ${MBPS_STRING}`;
  } else if (bps >= KBPS_CONVERSION) {
    return `${bpsToKbps(bps, numberOfDecimals, alternateNumberOfDecimals)} ${KBPS_STRING}`;
  } else if (bps > 0) {
    return `${bps.toFixed(numberOfDecimals)} ${BPS_STRING}`;
  } else {
    return `${bps.toFixed(0)} ${BPS_STRING}`;
  }
};
